<script setup>
import {computed, toRef, ref} from "vue";
import Image from "~/components/atoms/Image.vue";
import Price from "~/components/atoms/Price.vue";
import Modal from "~/components/molecules/Modal.vue";
import Button from "~/components/atoms/Button.vue";
import ExpandIcon from "~/components/icons/ExpandIcon.vue";
import {useCurrencyStore} from "~/store/currencyStore.js";
import {useBannerData} from "~/composables/useBannerData.js";

const props = defineProps({
  banner: {
    type: Object,
    default: () => ({})
  }
})

const currencyStore = useCurrencyStore();
const config = useRuntimeConfig();
const currentLocale = config?.public?.locale ?? 'sv';
const showModal = ref(false);

const getLocalizedText = (text) => {
  if (!text) return '';
  if (typeof text === 'string') return text;
  if (typeof text === 'object') {
    try {
      const localized = text[currentLocale] || text.sv || text.da || text.nb || Object.values(text)[0] || '';
      return localized;
    } catch (e) {
      console.warn('Error getting localized text:', e);
      return '';
    }
  }
  return '';
};

const bannerRef = toRef(props, 'banner');

const hasBannerData = computed(() => {
  return !!props.banner && typeof props.banner === 'object';
});

const {
  isVisible,
  bannerUrl,
  bannerSrc,
  bannerMobileSrc,
  bannerTitle,
  bannerBtnText,
  bannerPriceText,
  bannerPrice,
  bannerCrossPrice,
  bannerPosition,
  bannerBgColor,
  bannerFontColor,
  nameWidth,
  buttonBgColor,
  buttonTextColor,
  textBlockStyle,
  hoverEffect,
  underButtonText,
  underButtonUrl,
  underButtonStyle,
  bannerContent,
  bannerMobileContent,
  hasBannerContent,
  hasBannerMobileContent,
} = useBannerData(bannerRef, getLocalizedText);

const mapStyle = (style) => {
  if (style === 'bronze') return 'bronze';
  if (style === 'grey') return 'light-grey';
  return style || 'black';
};

const buttonStyle = computed(() => {
  return {
    backgroundColor: buttonBgColor.value,
    color: buttonTextColor.value,
    borderColor: 'transparent'
  };
});

const titleStyle = computed(() => {
  return {
    maxWidth: `${nameWidth.value}px`
  };
});

const handleImageClick = (e) => {
  if (hoverEffect.value) {
    e.stopPropagation();
    e.preventDefault();
    showModal.value = true;
  }
};
</script>

<template>
  <div v-if="hasBannerData">
    <div v-if="isVisible" class="big-banner full-width" :class="{ 'modal-open': showModal }">
      <div class="banner-wrapper" :class="{'banner-reverse': bannerPosition === 'right'}">
        <nuxt-link :to="bannerUrl || '/'" class="banner-block-text" :style="textBlockStyle">
          <Text class="banner-prices">
            <div v-if="bannerCrossPrice" class="banner-cross-price">
              <Price :price="bannerCrossPrice" :digits="0"/>
              {{ currencyStore?.currency || 'KR' }}
            </div>
            <Text class="banner-current-price">
              <span v-if="bannerPriceText" class="banner-price-text"
                    :class="{'has-price': bannerPrice}">{{ bannerPriceText }} </span>
              <Price v-if="bannerPrice" :price="bannerPrice" :digits="0"/>
              <span v-if="bannerPrice" style="margin-left: 5px;">{{ currencyStore?.currency || 'KR' }}</span>
            </Text>
          </Text>
          <div v-if="bannerTitle" class="banner-title" :style="titleStyle">
            {{ bannerTitle }}
          </div>
          <div v-if="bannerBtnText" class="banner-button" :style="buttonStyle">
            <span>{{ bannerBtnText }}</span>
          </div>
        </nuxt-link>

        <div v-if="bannerSrc" class="banner-block-image" :class="{'has-zoom': hoverEffect}" @click="handleImageClick">
          <nuxt-link v-if="!hoverEffect && bannerUrl" :to="bannerUrl" class="image-link-overlay" />
          <Image
              :static="true"
              loading="lazy"
              :src="bannerSrc"
              :mobile-src="bannerMobileSrc"
          />
          <div v-if="hoverEffect" class="zoom-btn">
            <img src="/img/web-icons/pl.png" alt="Zoom" class="white">
          </div>
        </div>
        <div
          v-if="hasBannerContent"
          class="banner-html-content banner-html-content--desktop"
          v-html="bannerContent"
        />
        <div
          v-if="hasBannerMobileContent"
          class="banner-html-content banner-html-content--mobile"
          v-html="bannerMobileContent"
        />
      </div>
      <div class="banner-under-button-wrapper" v-if="underButtonText">
        <nuxt-link 
          :to="underButtonUrl || '/'" 
          class="btn-link"
        >
          <Button 
            :type="mapStyle(underButtonStyle)" 
            class="tall full-width-mobile coarse"
            :class="{'uppercase': underButtonStyle === 'black'}"
          >
            {{ underButtonText }}
            <ExpandIcon v-if="underButtonStyle === 'grey'" stroke="black" class="icon-right" />
          </Button>
        </nuxt-link>
      </div>
    </div>

    <div v-else-if="bannerSrc" class="big-banner full-width" :class="{ 'modal-open': showModal }">
      <div class="banner-wrapper-simple" :class="{'has-zoom': hoverEffect}" @click="handleImageClick">
        <nuxt-link v-if="!hoverEffect && bannerUrl" :to="bannerUrl" class="image-link-overlay" />
        <Image
            :static="true"
            loading="lazy"
            :src="bannerSrc"
            :mobile-src="bannerMobileSrc"
        />
        <div v-if="hoverEffect" class="zoom-btn">
          <img src="/img/web-icons/pl.png" alt="Zoom" class="white">
        </div>
        <div
          v-if="hasBannerContent"
          class="banner-html-content banner-html-content--desktop"
          v-html="bannerContent"
        />
        <div
          v-if="hasBannerMobileContent"
          class="banner-html-content banner-html-content--mobile"
          v-html="bannerMobileContent"
        />
      </div>
      <div class="banner-under-button-wrapper" v-if="underButtonText">
        <nuxt-link 
          :to="underButtonUrl || '/'" 
          class="btn-link"
        >
          <Button 
            :type="mapStyle(underButtonStyle)" 
            class="tall full-width-mobile coarse"
            :class="{'uppercase': underButtonStyle === 'black'}"
          >
            {{ underButtonText }}
            <ExpandIcon v-if="underButtonStyle === 'grey'" stroke="black" class="icon-right" />
          </Button>
        </nuxt-link>
      </div>
    </div>

    <Modal no-heading no-padding v-if="showModal" @close="showModal=false" :width="740" :height="740" extraClass="medium-square">
        <img :src="bannerSrc" style="width: 100%; height: 100%; object-fit: cover;" />
    </Modal>
  </div>
</template>

<style scoped lang="scss">
.big-banner {
  width: 100%;
  display: block;
  height: fit-content;
  position: relative;
  cursor: pointer;
  text-decoration: none;

  @include tablets {
    margin-left: -15px;
    margin-right: -15px;
    width: calc(100% + 30px);
  }

  @include small-tablets {
    margin-left: -15px;
    margin-right: -15px;
    width: calc(100% + 30px);
  }

  @include mobile {
    margin-left: -15px;
    margin-right: -15px;
    width: calc(100% + 30px);
  }

  &.modal-open {
    z-index: 9999999999;
  }
}

.image-link-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
}

.big-banner-empty {
  display: none;
}

.banner-wrapper {
  display: flex !important;
  flex-direction: row;
  width: 100%;
  height: 290px;
  position: relative;

  &.banner-reverse {
    flex-direction: row-reverse;
  }

  @include medium {
    height: 250px;
  }

  @include tablets {
    flex-direction: row;
    height: 200px;
  }

  @include small-tablets {
    flex-direction: row;
    height: 170px;
  }

  @include mobile {
    flex-direction: row;
    height: 140px;
  }
}

.banner-block-text {
  flex: 0 0 35% !important;
  width: 35% !important;
  max-width: 35%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 30px 20px;
  height: 100%;
  text-decoration: none;
  color: inherit;

  @include medium {
    gap: 10px;
    padding: 25px 15px;
  }

  @include tablets {
    flex: 0 0 38% !important;
    width: 38% !important;
    max-width: 38%;
    padding: 15px 10px;
    gap: 6px;
  }

  @include small-tablets {
    flex: 0 0 40% !important;
    width: 40% !important;
    max-width: 40%;
    padding: 12px 8px;
    gap: 5px;
  }

  @include mobile {
    flex: 0 0 40% !important;
    width: 40% !important;
    max-width: 40%;
    padding: 8px 5px;
    gap: 4px;
  }
}

.banner-block-image {
  flex: 0 0 65% !important;
  width: 65% !important;
  max-width: 65%;
  min-width: 65%;
  position: relative;
  display: block;
  overflow: hidden;
  height: 100%;
  flex-shrink: 0 !important;

  @include tablets {
    flex: 0 0 62% !important;
    width: 62% !important;
    max-width: 62%;
    min-width: 62%;
  }

  @include small-tablets {
    flex: 0 0 60% !important;
    width: 60% !important;
    max-width: 60%;
    min-width: 60%;
  }

  @include mobile {
    flex: 0 0 60% !important;
    width: 60% !important;
    max-width: 60%;
    min-width: 60%;
  }

  :deep(.image-wrapper) {
    width: 100%;
    height: 100%;
    display: block;
  }

  :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }
}

.zoom-btn {
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 32px 48px rgba(82, 82, 82, .08);
  position: absolute;
  cursor: pointer;
  z-index: 9;
  opacity: 0;
  width: 71px;
  height: 71px;
  transition: opacity .2s ease-in-out;
  background: hsla(0, 0%, 100%, .9);
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);

  img {
    width: 25px;
    height: 25px;
  }

  @include smd {
    width: 40px;
    height: 40px;
    img {
      width: 16px;
      height: 16px;
    }
  }
}

.has-zoom:hover {
  .zoom-btn {
    opacity: 1;
    transition-delay: .1s;
  }
  :deep(img) {
    filter: brightness(0.9);
  }
}

.banner-wrapper-simple {
  position: relative;
  width: 100%;
  display: block;
  overflow: visible;
}

.banner-html-content {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 24px;

  :deep(a),
  :deep(button) {
    pointer-events: auto;
  }

  :deep(p) {
    margin: 0 0 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &--desktop {
    @include smd {
      display: none;
    }
  }

  &--mobile {
    display: none;

    @include smd {
      display: flex;
      padding: 12px;
    }
  }
}

.banner-title {
  font-size: 12px;
  font-weight: 300;
  letter-spacing: 0.5px;
  text-align: center;
  flex: 0;
  line-height: 1.5;

  @include medium {
    font-size: 11px;
    letter-spacing: 0.4px;
  }

  @include tablets {
    font-size: 10px;
    letter-spacing: 0.3px;
    line-height: 1.4;
    max-width: 100%;
  }

  @include small-tablets {
    font-size: 9px;
    letter-spacing: 0.2px;
    line-height: 1.35;
    max-width: 100%;
  }

  @include mobile {
    font-size: 8px;
    letter-spacing: 0.1px;
    color: white;
    font-weight: 400;
    line-height: 1.3;
    max-width: 100%;
  }
}

.banner-prices {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  @include tablets {
    gap: 1px;
  }

  @include small-tablets {
    gap: 1px;
  }

  @include mobile {
    gap: 1px;
  }
}

.banner-price-text {
  font-size: 20px;
  font-weight: 500;
  line-height: 1;
  text-transform: uppercase;
  display: block;
  margin: auto;
  text-align: center;

  &.has-price {
    margin-right: 6px;
  }

  @include medium {
    font-size: 18px;

    &.has-price {
      margin-right: 5px;
    }
  }

  @include tablets {
    font-size: 16px;
    letter-spacing: 0.4px;

    &.has-price {
      margin-right: 5px;
    }
  }

  @include small-tablets {
    font-size: 15px;
    letter-spacing: 0.45px;

    &.has-price {
      margin-right: 4px;
    }
  }

  @include mobile {
    font-size: 14px;
    letter-spacing: 0.5px;

    &.has-price {
      margin-right: 4px;
    }
  }
}

.banner-cross-price {
  font-size: 14px;
  font-weight: 400;
  text-decoration: line-through;
  opacity: 0.6;
  line-height: 1;

  @include medium {
    font-size: 12px;
  }

  @include tablets {
    font-size: 10px;
  }

  @include small-tablets {
    font-size: 9px;
  }

  @include mobile {
    font-size: 8px;
  }
}

.banner-current-price {
  font-size: 20px;
  font-weight: 500;
  line-height: 1;
  margin-top: 5px;

  @include medium {
    font-size: 18px;
    margin-top: 4px;
  }

  @include tablets {
    font-size: 16px;
    letter-spacing: 0.4px;
    margin-top: 4px;
  }

  @include small-tablets {
    font-size: 15px;
    letter-spacing: 0.45px;
    margin-top: 3px;
  }

  @include mobile {
    font-size: 14px;
    letter-spacing: 0.5px;
    margin-top: 3px;
  }
}

.banner-button {
  background: transparent;
  color: black;
  padding: 7px 12px 4px;
  flex: 0;
  font-size: 13px;
  background-color: white;
  font-weight: 800;
  cursor: pointer;
  border: 1px solid;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;

  span {
    line-height: 1.1;
    display: inline-block;
  }

  @include medium {
    font-size: 11px;
    padding: 7px 10px 3px;
    font-weight: 500;
  }

  @include tablets {
    font-size: 9px;
    padding: 6px 8px 3px;
    letter-spacing: 0.4px;
    font-weight: 500;
  }

  @include small-tablets {
    font-size: 7px;
    padding: 6px 6px 2px;
    letter-spacing: 0.45px;
    font-weight: 500;
  }

  @include mobile {
    padding: 3px 6px 2px;
    font-size: 6px;
    letter-spacing: 0.5px;
    font-weight: 500;
  }
}

.banner-under-button-wrapper {
  margin-top: 16px;
  display: flex;
  justify-content: center;

  @include smd {
    width: 358px;
    margin: 16px auto 0;
    max-width: 100%;
  }
}

.btn-link {
  text-decoration: none;
  display: block;
  width: fit-content;
  margin: 0 auto;

  @include smd {
    width: 100%;
    margin: 0;
  }

  &:deep(button) {
    min-width: 226px;
    margin: 0;

    &.bronze, &.light-grey {
      font-weight: 600 !important;
    }

    @include smd {
      width: 100%;
      min-width: 0;
    }
  }
}

.icon-right {
  margin-left: 10px;
  width: 16px;
  height: 16px;
}
</style>
